/**
 * Check-detail page (route `/checks/:id`, Req 12.1, 12.2, 12.4). It loads the Check's current
 * Check_State (`GET /v1/checks/{id}`) and its results over a selectable time range
 * (`GET /v1/checks/{id}/results?from&to`), then renders:
 *
 *   - the current up/down Check_State with latest status and consecutive-failure streak (12.1);
 *   - a history/trends summary — status counts, uptime %, and latency min/max/avg/p50/p95 — plus a
 *     latency sparkline (12.2), computed by the pure {@link buildHistoryView} helper;
 *   - a time-ordered results list, each row linking to the result-detail page;
 *   - a per-Global_Location aggregation table (12.4) via {@link buildLocationRows};
 *   - the Terraform adoption section — import command and resource stanza (terraform-provider Req 14.1),
 *     rendered client-side from the already-loaded Check, so it costs no request (Req 14.7).
 *
 * The current-state load is keyed on the check id; the results load additionally re-runs when the
 * range selector changes, so switching range refetches only the history — not the check.
 */

import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useApiClient } from '../api/use-api-client';
import type { CheckDto, CheckStateView, GetCheckResponse, ListResultsResponse } from '../api/types';
import { useCanWrite } from '../auth/use-can-write';
import { ProvenanceNotice, requiresAcknowledgement } from '../components/ProvenanceNotice';
import { OriginBadge, StateBadge, StatusBadge } from '../components/Badges';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { Sparkline } from '../components/Sparkline';
import { TerraformSection } from '../components/TerraformSection';
import { buildHistoryView, buildLocationRows } from '../lib/aggregation';
import { formatMs, formatPercent, formatTimestamp } from '../lib/format';
import { useResource } from '../lib/use-resource';
import { useWrite } from '../lib/use-write';

/** The selectable trend windows, newest data ending at "now". */
const RANGES = [
  { key: '24h', label: 'Last 24h', seconds: 24 * 60 * 60 },
  { key: '7d', label: 'Last 7d', seconds: 7 * 24 * 60 * 60 },
  { key: '30d', label: 'Last 30d', seconds: 30 * 24 * 60 * 60 },
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

/** Compute the `from`/`to` epoch-second query for a range, ending at the current time. */
function rangeQuery(key: RangeKey): { from: string; to: string } {
  const seconds = RANGES.find((r) => r.key === key)?.seconds ?? RANGES[0].seconds;
  const to = Math.floor(Date.now() / 1000);
  return { from: String(to - seconds), to: String(to) };
}

export function CheckDetailPage(): JSX.Element {
  const client = useApiClient();
  const { id } = useParams();
  const checkId = id ?? '';
  const [rangeKey, setRangeKey] = useState<RangeKey>('24h');

  const check = useResource<GetCheckResponse>(() => client.getCheck(checkId), [client, checkId]);
  const results = useResource<ListResultsResponse>(
    () => client.listCheckResults(checkId, rangeQuery(rangeKey)),
    [client, checkId, rangeKey],
  );

  if (check.status === 'loading') {
    return (
      <section>
        <BackLink />
        <LoadingState label="Loading check…" />
      </section>
    );
  }

  if (check.status === 'error') {
    return (
      <section>
        <BackLink />
        <h1>Check detail</h1>
        <ErrorState summary="Could not load this check." message={check.message} isAuthError={check.isAuthError} />
      </section>
    );
  }

  const { check: checkDto, state } = check.data;

  return (
    <section>
      <BackLink />
      <div className="section-header">
        <h1>{checkDto.logicalName}</h1>
        <CheckWriteActions check={checkDto} />
      </div>
      <p className="muted">
        {checkDto.type} · every {checkDto.intervalSeconds}s ·{' '}
        {checkDto.locations.length > 0 ? checkDto.locations.join(', ') : 'no locations'} ·{' '}
        <OriginBadge origin={checkDto.origin} />
      </p>

      <CurrentState state={state} />

      {/*
       * The Terraform adoption surface (terraform-provider Req 14.1), below the Check's configuration
       * summary and above the history, so it sits with "what this check is" rather than with "how it has
       * been behaving". It renders purely from `checkDto`, which is already loaded — no extra request
       * (Req 14.7).
       */}
      <TerraformSection subject={{ kind: 'check', check: checkDto }} />

      <div className="section-header">
        <h2>History &amp; trends</h2>
        <RangeSelector value={rangeKey} onChange={setRangeKey} />
      </div>

      <ResultsSection results={results} />
    </section>
  );
}

function BackLink(): JSX.Element {
  return (
    <p className="muted">
      <Link to="/">← All checks</Link>
    </p>
  );
}

/**
 * The Check's write affordances (granular-crud-api tasks 14.1, 14.2; Req 11.1, 11.6, 11.7, 11.8):
 * an edit link and a delete confirmation, rendered only for a role the API's matrix would permit
 * (Req 11.7 — display-only; the server enforces).
 *
 * Deletion cascades (Check_State, heartbeat, schedule membership, and the Check's firing Alerts all go
 * with it) and it is not recoverable from the dashboard, so it goes through {@link ConfirmDialog} — with
 * the {@link ProvenanceNotice} for the Check's origin: a manifest deploy recreates a deleted Check
 * (Req 11.8), and Terraform does the same on its next `apply` but **with a new id**, which starts the
 * Check's history and state over (terraform-provider Req 13.2). The Terraform case is gated on an
 * explicit acknowledgement before the delete may be issued (Req 13.3).
 *
 * A failed delete keeps the dialog open with the error and leaves this page untouched (Req 11.6); a
 * confirmed delete returns to the list.
 */
function CheckWriteActions({ check }: { check: CheckDto }): JSX.Element | null {
  const client = useApiClient();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const write = useWrite();
  const [confirming, setConfirming] = useState(false);
  /** The Terraform acknowledgement (Req 13.3), reset every time the dialog is opened. */
  const [acknowledged, setAcknowledged] = useState(false);

  if (!canWrite) return null;

  return (
    <div className="row-actions">
      <Link to={`/checks/${encodeURIComponent(check.checkId)}/edit`}>Edit check</Link>
      <button
        type="button"
        className="danger"
        onClick={() => {
          write.reset();
          // A fresh decision each time the dialog opens: an acknowledgement carried over from a
          // dismissed dialog would let the next delete through without one being made.
          setAcknowledged(false);
          setConfirming(true);
        }}
      >
        Delete check
      </button>
      {confirming ? (
        <ConfirmDialog
          title="Delete check"
          message="Deleting this check removes it from the schedule and deletes its current state and heartbeat. Recorded results are retained."
          confirmLabel="Delete check"
          busy={write.state.submitting}
          errorMessage={write.state.errorMessage}
          confirmDisabled={requiresAcknowledgement(check.origin) && !acknowledged}
          onConfirm={() =>
            void write.run(
              () => client.deleteCheck(check.checkId),
              () => {
                setConfirming(false);
                navigate('/');
              },
            )
          }
          onCancel={() => {
            write.reset();
            setConfirming(false);
          }}
        >
          <ProvenanceNotice
            origin={check.origin}
            entity="check"
            action="delete"
            acknowledged={acknowledged}
            onAcknowledge={setAcknowledged}
          />
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

function CurrentState({ state }: { state: CheckStateView }): JSX.Element {
  return (
    <div className="card current-state" data-testid="current-state">
      <div className="stat">
        <span className="stat-label">State</span>
        <span className="stat-value">
          <StateBadge state={state.state} hasResults={state.hasResults} />
        </span>
      </div>
      <div className="stat">
        <span className="stat-label">Latest result</span>
        <span className="stat-value">
          {state.latestStatus ? <StatusBadge status={state.latestStatus} /> : <span className="muted">—</span>}
        </span>
      </div>
      <div className="stat">
        <span className="stat-label">Consecutive failures</span>
        <span className="stat-value">{state.consecutiveFailures}</span>
      </div>
      <div className="stat">
        <span className="stat-label">Last updated</span>
        <span className="stat-value">
          {state.hasResults && state.updatedAt ? formatTimestamp(state.updatedAt) : <span className="muted">No results yet</span>}
        </span>
      </div>
    </div>
  );
}

function RangeSelector({ value, onChange }: { value: RangeKey; onChange: (key: RangeKey) => void }): JSX.Element {
  return (
    <div className="range-selector" role="group" aria-label="Time range">
      {RANGES.map((range) => (
        <button
          key={range.key}
          type="button"
          className={range.key === value ? 'range-option range-active' : 'range-option'}
          aria-pressed={range.key === value}
          onClick={() => onChange(range.key)}
        >
          {range.label}
        </button>
      ))}
    </div>
  );
}

function ResultsSection({ results }: { results: ReturnType<typeof useResource<ListResultsResponse>> }): JSX.Element {
  if (results.status === 'loading') {
    return <LoadingState label="Loading results…" />;
  }
  if (results.status === 'error') {
    return <ErrorState summary="Could not load results." message={results.message} isAuthError={results.isAuthError} />;
  }

  const rows = results.data.results;
  if (rows.length === 0) {
    return <p className="muted">No results recorded in this time range.</p>;
  }

  const history = buildHistoryView(rows);
  const locations = buildLocationRows(rows);

  return (
    <>
      <div className="card trends-summary" data-testid="trends-summary">
        <div className="stat">
          <span className="stat-label">Results</span>
          <span className="stat-value">{history.total}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Uptime</span>
          <span className="stat-value">{formatPercent(history.uptimePct)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Passing / Degraded / Failing</span>
          <span className="stat-value">
            {history.statusCounts.passing} / {history.statusCounts.degraded} / {history.statusCounts.failing}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Latency avg</span>
          <span className="stat-value">{formatMs(history.latency.avgMs)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Latency min / max</span>
          <span className="stat-value">
            {formatMs(history.latency.minMs)} / {formatMs(history.latency.maxMs)}
          </span>
        </div>
        <div className="stat">
          <span className="stat-label">Latency p50 / p95</span>
          <span className="stat-value">
            {formatMs(history.latency.p50Ms)} / {formatMs(history.latency.p95Ms)}
          </span>
        </div>
        <div className="stat stat-wide">
          <span className="stat-label">Latency trend</span>
          <span className="stat-value trend-accent">
            <Sparkline values={history.series.map((p) => p.durationMs)} label="Latency over time" />
          </span>
        </div>
      </div>

      <LocationTable rows={locations} />

      <h2>Results</h2>
      <table className="checks-table">
        <thead>
          <tr>
            <th scope="col">Time</th>
            <th scope="col">Location</th>
            <th scope="col">Status</th>
            <th scope="col">Duration</th>
          </tr>
        </thead>
        <tbody>
          {/* Newest first for the log; the sparkline above stays in ascending time order. */}
          {[...history.series].reverse().map((point) => (
            <tr key={point.resultId}>
              <td>
                <Link to={`/results/${encodeURIComponent(point.resultId)}`}>{formatTimestamp(point.timestamp)}</Link>
              </td>
              <td>{point.location}</td>
              <td>
                <StatusBadge status={point.status} />
              </td>
              <td>{formatMs(point.durationMs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function LocationTable({ rows }: { rows: ReturnType<typeof buildLocationRows> }): JSX.Element {
  return (
    <>
      <h2>By location</h2>
      <table className="checks-table" aria-label="Per-location results">
        <thead>
          <tr>
            <th scope="col">Location</th>
            <th scope="col">Results</th>
            <th scope="col">Uptime</th>
            <th scope="col">Avg</th>
            <th scope="col">p95</th>
            <th scope="col">Latest</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.location}>
              <td>{row.location}</td>
              <td>{row.total}</td>
              <td>{formatPercent(row.uptimePct)}</td>
              <td>{formatMs(row.latency.avgMs)}</td>
              <td>{formatMs(row.latency.p95Ms)}</td>
              <td>{row.latestStatus ? <StatusBadge status={row.latestStatus} /> : <span className="muted">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
